import type { ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { Display } from '@/components/ui/Typography';
import { StationCard } from '@/components/stations/StationCard';
import { AliancaHomeCard } from '@/components/alianca/AliancaHomeCard';
import type { StationDetailOut } from '@/api/stations';

type LoadState = 'loading' | 'ready' | 'error';

interface Props {
  stations: StationDetailOut[];
  status: LoadState;
  error: string | null;
  onRetry: () => void;
  distances: Map<string, number>;
  /** Aviso de localização (Home decide quando) — fica acima da lista. */
  locationNudge?: ReactNode;
  showAliancaCaptacao: boolean;
}

/**
 * Lista clássica de estações — extraída do antigo shell da Home (map-first,
 * 2026-07-05) pra servir de fallback "Ver lista" quando o usuário prefere não
 * usar o mapa, ou quando não há chave do Google Maps configurada.
 */
export function HomeStationList({
  stations,
  status,
  error,
  onRetry,
  distances,
  locationNudge,
  showAliancaCaptacao
}: Props) {
  return (
    <section className="flex min-h-0 flex-1 flex-col gap-3 [@media(max-height:740px)]:gap-2.5">
      <div className="mt-1 flex flex-shrink-0 items-baseline justify-between [@media(max-height:740px)]:mt-0">
        <Display size={20} as="h2">
          Eletropostos
        </Display>
        {status === 'ready' && stations.length > 0 && (
          <span className="text-[13px] text-bow-royal/55">
            {stations.length} {stations.length === 1 ? 'estação' : 'estações'}
          </span>
        )}
      </div>

      {locationNudge && <div className="flex-shrink-0">{locationNudge}</div>}

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pb-1 [@media(max-height:740px)]:gap-2.5">
        {status === 'loading' && <StationsSkeleton />}

        {status === 'error' && (
          <div className="flex flex-col items-start gap-3 rounded-3xl bg-white p-5 shadow-soft">
            <p className="text-sm text-bow-red">{error}</p>
            <Button onClick={onRetry} fullWidth={false} size="md">
              Tentar novamente
            </Button>
          </div>
        )}

        {status === 'ready' && stations.length === 0 && (
          <p className="text-sm text-bow-royal/65">Nenhum eletroposto cadastrado.</p>
        )}

        {status === 'ready' &&
          stations.map((s, i) => (
            <StationCard
              key={s.id}
              station={s}
              distanceKm={distances.get(s.id) ?? null}
              nearest={i === 0 && distances.has(s.id)}
            />
          ))}
      </div>

      {showAliancaCaptacao && (
        <div className="flex-shrink-0">
          <AliancaHomeCard />
        </div>
      )}
    </section>
  );
}

function StationsSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      {[0, 1].map((i) => (
        <div key={i} className="h-28 animate-pulse rounded-3xl bg-white shadow-soft" />
      ))}
    </div>
  );
}
